import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../config';

interface SearchResult {
  segment_id: string;
  video_id: string;
  video_name: string;
  video_file: string;
  text: string;
  start_time: number;
  end_time: number;
  duration?: number;
  summary?: string;
  segment_categories?: string[];
  video_categories?: string[];
  topics_discussed?: string[];
  labels?: string[];
  score?: number;
  classification_terms?: string[];
  content_type?: string;
  tone?: string;
  subject_keywords?: string[];
  thumbnail_path?: string;
}

interface ElasticsearchHit {
  _source: SearchResult;
  _score: number;
  _index: string;
  _type?: string;
  _id: string;
}

interface ElasticsearchResponse {
  hits: {
    hits: ElasticsearchHit[];
    total: {
      value: number;
      relation: string;
    };
    max_score: number;
  };
  took: number;
  timed_out: boolean;
}

const Search: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ElasticsearchHit[]>([]);
  const [searchMeta, setSearchMeta] = useState<{total: number, took: number, maxScore: number} | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<ElasticsearchHit | null>(null);
  const navigate = useNavigate();

  // Clear results when query is empty
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSearchMeta(null);
      setError(null);
    }
  }, [query]);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    console.log(`[Search] Starting search for: "${query}" at ${new Date().toISOString()}`);
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('/api/v1/search', {
        params: {
          query: query,
          size: 100,
          t: Date.now() // Cache buster
        },
        withCredentials: false, // Avoid CORS requiring Allow-Credentials
      });
      
      // Extract search results from Elasticsearch response structure
      const esResponse: ElasticsearchResponse = response.data.data;
      const searchHits: ElasticsearchHit[] = esResponse?.hits?.hits || [];
      
      // Set metadata
      setSearchMeta({
        total: esResponse?.hits?.total?.value || 0,
        took: esResponse?.took || 0,
        maxScore: esResponse?.hits?.max_score || 0
      });
      
      console.log(`[Search] Found ${searchHits.length} results for: "${query}"`);
      console.log('[Search] Total available:', esResponse?.hits?.total?.value);
      console.log('[Search] Search took:', esResponse?.took, 'ms');
      console.log('[Search] First result:', searchHits[0]);
      setResults(searchHits);
    } catch (err) {
      console.error(`[Search] Error searching for: "${query}"`, err);
      if (axios.isAxiosError(err)) {
        console.error('[Search] Response data:', err.response?.data);
        console.error('[Search] Response status:', err.response?.status);
      }
      setError('Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  const navigateToVideo = (hit: ElasticsearchHit) => {
    const result = hit._source;
    navigate(`/video/${result.video_id}?start=${result.start_time}`);
  };

  const handleDocumentClick = (hit: ElasticsearchHit, event: React.MouseEvent) => {
    // If clicking on play button or similar, don't show document
    if ((event.target as HTMLElement).closest('.play-button')) {
      return;
    }
    
    // If shift key is held, show document details
    if (event.shiftKey) {
      setSelectedDocument(hit);
      return;
    }
    
    // Otherwise, navigate to video
    navigateToVideo(hit);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Video Search</h1>
      
      <form onSubmit={handleSearch} style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for content..."
            style={{
              flex: 1,
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {error && (
        <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>
      )}

      {searchMeta && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '10px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '4px',
          fontSize: '14px',
          color: '#666'
        }}>
          <strong>Search Results:</strong> {searchMeta.total} total results found in {searchMeta.took}ms 
          {searchMeta.maxScore > 0 && ` (max score: ${searchMeta.maxScore.toFixed(3)})`}
          {results.length < searchMeta.total && (
            <span style={{ color: '#dc3545' }}> - Showing first {results.length} results</span>
          )}
          <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
            💡 Click to play video • Shift+Click to view full document
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
        {results.map((hit, index) => {
          const result = hit._source;
          return (
            <div
              key={hit._id || `${result.video_id}-${index}`}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                backgroundColor: '#f9f9f9',
                position: 'relative'
              }}
              onClick={(event) => handleDocumentClick(hit, event)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Elasticsearch Score Badge */}
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                backgroundColor: '#007bff',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: 'bold'
              }}>
                Score: {hit._score.toFixed(2)}
              </div>

              {result.thumbnail_path && (
                <img
                  src={result.thumbnail_path}
                  alt="Video thumbnail"
                  style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '10px' }}
                />
              )}
            <div style={{ marginBottom: '10px' }}>
              <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', fontWeight: 'bold' }}>
                {result.video_name || result.video_id}
              </h4>
              <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                {formatTime(result.start_time)} - {formatTime(result.end_time)}
              </p>
            </div>
            <p style={{ margin: '10px 0', fontSize: '14px', lineHeight: '1.4' }}>
              {result.text}
            </p>
            {result.summary && (
              <p style={{ margin: '5px 0', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                Summary: {result.summary}
              </p>
            )}
            {result.segment_categories && result.segment_categories.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {result.segment_categories.map((category, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#e9ecef',
                      color: '#495057',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      marginRight: '4px',
                      marginBottom: '2px'
                    }}
                  >
                    {category}
                  </span>
                ))}
              </div>
            )}
            {result.topics_discussed && result.topics_discussed.length > 0 && (
              <div style={{ marginTop: '5px' }}>
                <span style={{ fontSize: '10px', color: '#666' }}>Topics: </span>
                {result.topics_discussed.slice(0, 5).map((topic, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#f8f9fa',
                      color: '#6c757d',
                      padding: '1px 4px',
                      borderRadius: '2px',
                      fontSize: '10px',
                      marginRight: '3px'
                    }}
                  >
                    {topic}
                  </span>
                ))}
                {result.topics_discussed.length > 5 && (
                  <span style={{ fontSize: '10px', color: '#999' }}>
                    +{result.topics_discussed.length - 5} more
                  </span>
                )}
              </div>
            )}
            
            {/* Classification Terms */}
            {result.classification_terms && result.classification_terms.length > 0 && (
              <div style={{ marginTop: '5px' }}>
                <span style={{ fontSize: '10px', color: '#666' }}>Classification: </span>
                {result.classification_terms.slice(0, 6).map((term, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#e7f3ff',
                      color: '#0066cc',
                      padding: '1px 4px',
                      borderRadius: '2px',
                      fontSize: '10px',
                      marginRight: '3px'
                    }}
                  >
                    {term}
                  </span>
                ))}
                {result.classification_terms.length > 6 && (
                  <span style={{ fontSize: '10px', color: '#999' }}>
                    +{result.classification_terms.length - 6} more
                  </span>
                )}
              </div>
            )}
            
            {/* Subject Keywords */}
            {result.subject_keywords && result.subject_keywords.length > 0 && (
              <div style={{ marginTop: '5px' }}>
                <span style={{ fontSize: '10px', color: '#666' }}>Keywords: </span>
                {result.subject_keywords.slice(0, 8).map((keyword, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      padding: '1px 4px',
                      borderRadius: '2px',
                      fontSize: '10px',
                      marginRight: '3px'
                    }}
                  >
                    {keyword}
                  </span>
                ))}
                {result.subject_keywords.length > 8 && (
                  <span style={{ fontSize: '10px', color: '#999' }}>
                    +{result.subject_keywords.length - 8} more
                  </span>
                )}
              </div>
            )}
            
            {/* Content Type and Tone */}
            {(result.content_type || result.tone) && (
              <div style={{ marginTop: '5px' }}>
                {result.content_type && (
                  <span style={{
                    display: 'inline-block',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    fontSize: '10px',
                    marginRight: '3px'
                  }}>
                    Type: {result.content_type}
                  </span>
                )}
                {result.tone && (
                  <span style={{
                    display: 'inline-block',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    fontSize: '10px',
                    marginRight: '3px'
                  }}>
                    Tone: {result.tone}
                  </span>
                )}
              </div>
            )}
            
            {/* Elasticsearch Metadata */}
            <div style={{ 
              marginTop: '10px', 
              paddingTop: '8px', 
              borderTop: '1px solid #eee', 
              fontSize: '10px', 
              color: '#999' 
            }}>
              <div>ES ID: {hit._id}</div>
              <div>Index: {hit._index}</div>
              {hit._type && <div>Type: {hit._type}</div>}
              <div>Segment ID: {result.segment_id}</div>
              {result.score && <div>Internal Score: {result.score.toFixed(3)}</div>}
            </div>
          </div>
          );
        })}
      </div>

      {results.length === 0 && !loading && query && (
        <div style={{ textAlign: 'center', color: '#666', marginTop: '40px' }}>
          No results found. Try a different search term.
        </div>
      )}

      {/* Document Details Modal */}
      {selectedDocument && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setSelectedDocument(null)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '20px',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#666',
                padding: '5px'
              }}
              onClick={() => setSelectedDocument(null)}
            >
              ×
            </button>
            
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Full Elasticsearch Document</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <button
                className="play-button"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
                onClick={() => {
                  setSelectedDocument(null);
                  navigateToVideo(selectedDocument);
                }}
              >
                ▶ Play Video
              </button>
            </div>
            
            <pre style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '4px',
              fontSize: '12px',
              lineHeight: '1.4',
              overflow: 'auto',
              border: '1px solid #e9ecef'
            }}>
              {JSON.stringify(selectedDocument, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default Search;